/**
 * The map surface.
 *
 * **Occupancy is the rule; a position is an opt-in second picture over it.**
 * Front/flank/rear zones are what every other combat rule reads and are
 * always shown as prose (`ZoneOccupancy`) — a fight the Owner never gave a
 * scaled map behaves exactly as it always has. A fight that *did* also gets
 * real tokens (`CombatantTokens`), placed by a `move` declaration and never
 * dragged: `zone:update` carries both, fog arrives on `fog:update`, the
 * images on `map:update`, and a waypoint pin is a *join* rather than a
 * position anyone edits.
 *
 * Which mode is active is the server's decision (`state:mode`) — it is what
 * everyone's shared, in-scene view is showing. Theatre is the default and
 * renders no map at all, which is the point: combat works with no image, so
 * an absent upload costs the table nothing.
 *
 * **`WorldMap` is exported separately, on purpose.** The world map itself is
 * not scene-scoped the way `mode` is: a character standing in a dungeon
 * theatre-of-the-mind scene can still want to check where they are on the
 * regional map, independent of what the DM currently has everyone else
 * looking at. The Map tab renders `WorldMap` directly for exactly this,
 * regardless of `mode` — the shared mode is unaffected by one player
 * checking their own copy.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';

import type { MapMode, MapSurface, Waypoint } from '@portal/shared';

import type { CombatView } from '../realtime/combat.js';
import type { MapLine } from './places.js';
import { zoneProse } from '../realtime/combat.js';
import { TerrainLayer } from './TerrainLayer.js';
import { TravelConfirm } from './TravelConfirm.js';
import {
  TERRAIN_COLOUR,
  UNMAPPED_COLOUR,
  terrainTally,
  unrecognisedTerrains,
  type TerrainGridView,
} from './terrain-palette.js';

export interface MapPanelProps {
  readonly mode: MapMode;
  readonly map: MapSurface;
  readonly waypoints: readonly Waypoint[];
  /**
   * Told where on the image the Owner clicked, in percentages.
   *
   * Percentages rather than pixels, because that is what a waypoint stores —
   * a map replaced with a better drawing keeps every pin it had. Absent for
   * everyone but the Owner, so a player clicking the map does nothing.
   */
  readonly onPickPoint?: (point: { mapX: number; mapY: number }) => void;
  /** The canon place id the pin sits on, joined onto `waypoints`. */
  readonly partyLocation?: string | undefined;
  /** Rivers and roads out of an imported world. Empty when none was imported. */
  readonly lines?: readonly MapLine[];
  /** The imported world's ground. Absent when no world was imported. */
  readonly terrain?: TerrainGridView | undefined;
  readonly combat: CombatView | null;
  /**
   * Makes the named pins buttons: a click asks "travel there?" with the journey's cost and goes only when
   * confirmed (Maz, 2026-10-21). Both are needed for it; absent, the pins are plain markers as before.
   */
  readonly campaignId?: string | undefined;
  readonly onTravel?: ((placeId: string) => void) | undefined;
}

/**
 * How each kind of line is drawn.
 *
 * **The four are told apart by eye, because they mean different numbers.** A
 * river costs +1 a mile and a stream +0.5; a road halves the ground and a trail
 * halves it less. Drawing them alike would put an Owner in the position of
 * being told a march took nine days with no way to see why.
 *
 * Water is blue and continuous; ways are dashed, because a road on a drawn map
 * is a route rather than a feature and the dashes stop it reading as a river.
 */
const LINE_STYLE: Readonly<
  Record<MapLine['kind'], { stroke: string; width: number; dash?: string }>
> = {
  river: { stroke: 'rgb(56 128 196 / 0.85)', width: 0.55 },
  stream: { stroke: 'rgb(96 165 214 / 0.7)', width: 0.3 },
  road: { stroke: 'rgb(180 140 90 / 0.9)', width: 0.45, dash: '1.6 1' },
  trail: { stroke: 'rgb(180 140 90 / 0.6)', width: 0.25, dash: '0.7 1.1' },
};

/**
 * Rivers and roads, drawn in the same 0-100 space as the pins.
 *
 * **A `viewBox` of `0 0 100 100` with `preserveAspectRatio="none"`** is what
 * makes a percentage coordinate land in the same place as a pin's `left: x%`.
 * The alternative - converting to pixels - would need the image's measured size
 * and would be wrong for one frame after every resize.
 *
 * `pointer-events: none` because the frame underneath is a *button* on the
 * Owner's screen: a river that swallowed clicks would make the map
 * un-annotatable wherever the water runs.
 *
 * A line of one point is dropped rather than drawn: an export occasionally
 * carries a degenerate feature, and `polyline` renders it as nothing visible
 * while still counting toward the total the panel reports.
 */
export function MapLines({ lines }: { readonly lines: readonly MapLine[] }) {
  const drawable = lines.filter((line) => line.points.length > 1);
  if (drawable.length === 0) return null;
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
    >
      {drawable.map((line, index) => {
        const style = LINE_STYLE[line.kind];
        return (
          <polyline
            key={`${line.kind}-${line.name ?? index}-${index}`}
            points={line.points.map((p) => `${p.mapX},${p.mapY}`).join(' ')}
            fill="none"
            stroke={style.stroke}
            strokeWidth={style.width}
            strokeLinecap="round"
            strokeLinejoin="round"
            {...(style.dash ? { strokeDasharray: style.dash } : {})}
          />
        );
      })}
    </svg>
  );
}

export function MapPanel({
  mode,
  map,
  waypoints,
  partyLocation,
  lines = [],
  terrain,
  combat,
  onPickPoint,
  campaignId,
  onTravel,
}: MapPanelProps) {
  // Theatre of the Mind is a first-class surface, not a fallback: the party
  // roster and the transcript are the whole of it.
  if (mode === 'theatre') return null;

  if (mode === 'worldMap') {
    return (
      <section aria-label="World map" className="space-y-2">
        <WorldMap
          map={map}
          waypoints={waypoints}
          lines={lines}
          {...(terrain ? { terrain } : {})}
          {...(partyLocation ? { partyLocation } : {})}
          {...(onPickPoint ? { onPickPoint } : {})}
          {...(campaignId && onTravel ? { campaignId, onTravel } : {})}
        />
      </section>
    );
  }

  // Encounter and Combat grids are the same picture; the combat chrome that
  // distinguishes them lives in CombatPanel, which is already on screen.
  return (
    <section aria-label="Encounter map" className="space-y-2">
      <MapImage
        src={map.encounterMapUrl}
        alt="The encounter map"
        empty="No encounter map — the fight runs on zones either way."
        pickLabel="Choose your destination on the map"
        {...(onPickPoint ? { onPickPoint } : {})}
      >
        {combat && <CombatantTokens combat={combat} />}
      </MapImage>
      {combat && <ZoneOccupancy combat={combat} />}
    </section>
  );
}

/**
 * The world map: the ground, the lines over it, the pins over those.
 *
 * Split out of `MapPanel` because it is the one branch with state of its own —
 * whether the ground is drawn. Local `useState` rather than the store, because
 * it is a preference about this screen and nothing else on the table needs to
 * know: two players may reasonably want different answers at the same moment.
 */
export function WorldMap({
  map,
  waypoints,
  lines,
  terrain,
  partyLocation,
  onPickPoint,
  campaignId,
  onTravel,
}: {
  readonly map: MapSurface;
  readonly waypoints: readonly Waypoint[];
  readonly lines: readonly MapLine[];
  readonly terrain?: TerrainGridView | undefined;
  readonly partyLocation?: string | undefined;
  readonly onPickPoint?: (point: { mapX: number; mapY: number }) => void;
  readonly campaignId?: string;
  readonly onTravel?: (placeId: string) => void;
}) {
  // Which pin was clicked, awaiting a confirmation. Local: it is this player's question, nobody else's.
  const [asking, setAsking] = useState<Waypoint | undefined>(undefined);
  // **On when there is no drawing, off when there is one — and it keeps
  // following the drawing until somebody says otherwise.**
  //
  // The terrain is painted *over* the uploaded image — there is no layer beneath
  // it — so on a hand-drawn map it is a tint across somebody's artwork, and
  // defaulting it on would mean every Owner who uploaded a picture first sees
  // it discoloured. With no image it is strictly better than the empty grid it
  // replaces: it is the only thing on that screen that shows the world at all.
  //
  // Hence the tri-state rather than an initial value: the image is uploaded from
  // the admin panel on *this* screen, so `map:update` lands without this
  // component remounting, and a default read once at mount would tint the
  // artwork the instant it arrived — the exact thing the paragraph above rules
  // out. An explicit tick or untick wins from then on, because at that point the
  // Owner has said something and the default has not.
  const [choice, setChoice] = useState<boolean | undefined>(undefined);
  const showTerrain = terrainVisible(choice, !!map.worldMapUrl);
  // Twenty thousand cells a sweep, so not on every render: this component
  // re-renders with the page, which is every transcript line, roll and presence
  // tick, and the answer changes only when a world is imported.
  const tally = useMemo(
    () => (terrain && showTerrain ? terrainTally(terrain) : []),
    [terrain, showTerrain],
  );
  // Off the legend, so this is twenty lookups rather than a sweep — cheap enough
  // that it does not need the layer to be showing to be worth computing.
  const uncoloured = useMemo(() => (terrain ? unrecognisedTerrains(terrain) : []), [terrain]);

  return (
    <div className="space-y-2">
      <MapImage
        src={map.worldMapUrl}
        alt="The world map"
        empty="No image yet — place waypoints on the grid, and an image dropped in later keeps every one of them."
        {...(onPickPoint ? { onPickPoint } : {})}
      >
        {/* Bottom of the stack: the ground is what the lines cross and the pins
            stand on, so it must not sit over either. */}
        {terrain && showTerrain && <TerrainLayer grid={terrain} />}
        {/* Under the pins deliberately: a waypoint is what the party is
            aiming at, and geography must not sit on top of it. */}
        <MapLines lines={lines} />
        {waypoints.map((waypoint) => (
          <Pin
            key={waypoint.placeId}
            waypoint={waypoint}
            here={waypoint.placeId === partyLocation}
            chosen={asking?.placeId === waypoint.placeId}
            // The place the party is standing at is not somewhere to travel to.
            {...(campaignId && onTravel && waypoint.placeId !== partyLocation ? { onChoose: () => setAsking(waypoint) } : {})}
          />
        ))}
      </MapImage>
      {asking && campaignId && onTravel && (
        <TravelConfirm
          campaignId={campaignId}
          placeId={asking.placeId}
          placeName={asking.name}
          onTravel={(placeId) => {
            setAsking(undefined);
            onTravel(placeId);
          }}
          onCancel={() => setAsking(undefined)}
        />
      )}
      {terrain && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <label className="flex items-center gap-1 text-xs text-neutral-600 dark:text-neutral-400">
            <input
              type="checkbox"
              checked={showTerrain}
              onChange={(event) => setChoice(event.target.checked)}
            />
            Show terrain
          </label>
          {showTerrain && <TerrainKey tally={tally} />}
        </div>
      )}
      {terrain && showTerrain && uncoloured.length > 0 && (
        // **Said out loud, because grey is not distinguishable from grey.** A
        // glacier and a tundra are both pale, so ground this palette could not
        // colour reads as ordinary map rather than as a hole in it. Normally
        // impossible — the completeness test covers every printed terrain — so
        // this names a grid written before a terrain existed, or edited by hand.
        <p className="text-xs text-amber-700 dark:text-amber-500">
          Drawn grey, because this map has no colour for it: {uncoloured.join(', ')}.
        </p>
      )}
      {waypoints.length === 0 && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          {/* Fog only uncovers, so an empty list is "nothing seen yet"
              rather than "nothing exists". */}
          Nowhere on this map has been charted yet.
        </p>
      )}
    </div>
  );
}

/**
 * Whether the ground is drawn: the Owner's choice, or the drawing's absence.
 *
 * **Extracted because the bug it fixes is invisible to a rendering test.** As
 * `useState(!worldMapUrl)` this produced byte-identical markup — the initial
 * value is only read at mount, and a static render only ever mounts — so a
 * revert would pass every test in the file. The failure needs a *second* render
 * with a new image and no remount, which is the one thing `renderToStaticMarkup`
 * cannot do. As a function of both inputs the rule is checkable directly, which
 * is the same reason `pointFromClick` and `interruptedCasts` are functions.
 *
 * `undefined` is not `false`: it means the Owner has not said, so the default
 * keeps tracking the image. Collapsing the two loses that distinction, and the
 * upload path is exactly where it matters.
 */
export function terrainVisible(choice: boolean | undefined, hasDrawing: boolean): boolean {
  return choice ?? !hasDrawing;
}

/**
 * Which colour is which ground, biggest first.
 *
 * **Only what is actually on the map, not the book's twenty.** A key listing
 * terrains the import never found is a key a reader has to search. Capped,
 * because a generated world routinely carries a dozen and the long tail is a
 * handful of cells apiece — the tail is still drawn, it is just not named here.
 */
function TerrainKey({ tally }: { readonly tally: readonly { name: string; cells: number }[] }) {
  if (tally.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-x-2 gap-y-0.5 text-[0.65rem] text-neutral-600 dark:text-neutral-400">
      {tally.slice(0, 8).map(({ name }) => (
        <li key={name} className="flex items-center gap-1">
          <span
            aria-hidden
            className="inline-block h-2 w-2 rounded-sm border border-neutral-400/50"
            style={{ backgroundColor: swatch(name) }}
          />
          {terrainLabel(name)}
        </li>
      ))}
    </ul>
  );
}

function swatch(terrain: string): string {
  const colour = TERRAIN_COLOUR[terrain] ?? UNMAPPED_COLOUR;
  return `rgb(${colour[0]} ${colour[1]} ${colour[2]})`;
}

/**
 * `forestHeavy` as "forest, heavy".
 *
 * The keys are the book's own and are camel-cased for lookup, not for reading.
 * Split rather than tabulated: a second table of twenty display names is a
 * second thing to keep in step with the first, for no more information than the
 * split already carries.
 */
function terrainLabel(terrain: string): string {
  return terrain
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .trim()
    .replace(' ', ', ');
}

/**
 * The image, or an honest statement that there isn't one.
 *
 * `position: relative` on the frame is what makes percentage-placed pins land
 * where the coordinates say — they are percentages *of the image*, which is
 * why they survive it being replaced with a differently-sized one.
 */
function MapImage({
  src,
  alt,
  empty,
  onPickPoint,
  pickLabel = 'Place a waypoint on the map',
  children,
}: {
  readonly src?: string | undefined;
  readonly alt: string;
  readonly empty: string;
  readonly onPickPoint?: (point: { mapX: number; mapY: number }) => void;
  /** What a click *does*, for a screen reader — a waypoint by default. */
  readonly pickLabel?: string;
  readonly children?: ReactNode;
}) {
  // **A broken image is not a shorter image — it is no image, and has to be
  // treated as one.** A stale or deleted asset key left the `<img>` tag on
  // the page with nothing to show: a browser gives a failed image no
  // intrinsic size, so the `w-full` container collapses to a sliver a few
  // pixels tall, and every waypoint `Pin` positioned as a percentage of that
  // sliver crushes into the same few pixels — pins and their labels stacked
  // on top of each other and the browser's own broken-image alt text,
  // unreadable. Falls back to the identical grid `!src` already uses, since
  // a failed load and an absent upload are the same problem for layout
  // purposes; only the state that shows it needs to be reset if `src` is
  // then repointed at something that actually loads.
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  // **A grid is a poor map, not no map.**
  //
  // Refusing to draw anything until an image was uploaded left an Owner with no
  // drawing unable to place a single waypoint, and so unable to travel at all.
  // A grid takes clicks exactly as a picture does, and an image dropped in
  // later keeps every pin, because the pins are percentages.
  //
  // It is genuinely worse, though, and not merely plainer: a picture shows a
  // reader the volcano the coordinates cannot, and the engine sees only what
  // somebody has declared about a region. The grid gets a party travelling; it
  // does not make the image optional.
  if (!src || failed) {
    return (
      <div className="space-y-1">
        <div
          className="relative overflow-hidden rounded-md border border-dashed border-neutral-300 dark:border-neutral-700"
          style={{
            aspectRatio: '2 / 1',
            backgroundSize: '10% 20%',
            backgroundImage:
              'linear-gradient(to right, rgb(148 163 184 / 0.25) 1px, transparent 1px),' +
              'linear-gradient(to bottom, rgb(148 163 184 / 0.25) 1px, transparent 1px)',
          }}
        >
          {onPickPoint ? (
            <button
              type="button"
              aria-label={pickLabel}
              className="absolute inset-0 h-full w-full cursor-crosshair"
              onClick={(event) =>
                onPickPoint(
                  pointFromClick(
                    event.currentTarget.getBoundingClientRect(),
                    event.clientX,
                    event.clientY,
                  ),
                )
              }
            />
          ) : null}
          {children}
        </div>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">{empty}</p>
      </div>
    );
  }
  if (!onPickPoint) {
    return (
      <div className="relative overflow-hidden rounded-md border border-neutral-200 dark:border-neutral-800">
        <img src={src} alt={alt} className="block w-full" onError={() => setFailed(true)} />
        {children}
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-md border border-neutral-200 dark:border-neutral-800">
      {/* **A button, not a div with a click handler.** The Owner may be using a
          keyboard, and an image you can only annotate with a mouse is one a
          keyboard user cannot annotate at all. Enter picks the centre, which
          is a starting point they can then correct by typing. */}
      <button
        type="button"
        aria-label={pickLabel}
        className="block w-full cursor-crosshair"
        onClick={(event) => {
          onPickPoint(
            pointFromClick(event.currentTarget.getBoundingClientRect(), event.clientX, event.clientY),
          );
        }}
      >
        <img src={src} alt={alt} className="block w-full" onError={() => setFailed(true)} />
      </button>
      {children}
    </div>
  );
}

/**
 * Where on the image a click landed, as percentages.
 *
 * **Percentages of the rendered box, not pixels.** That is what a waypoint
 * stores, and it is why a map replaced with a better drawing keeps every pin
 * it had — the same figure whatever size the image happens to be shown at.
 * Storing pixels would tie every pin to one rendering of one image at one
 * window width.
 *
 * Extracted so it can be tested: a static render never clicks anything, and
 * this is arithmetic that is wrong in a way no rendering test would notice.
 */
export function pointFromClick(
  box: { left: number; top: number; width: number; height: number },
  clientX: number,
  clientY: number,
): { mapX: number; mapY: number } {
  // Clamped: a click on the border can land a fraction outside, and a
  // waypoint at -0.2% is one the server refuses for being off the map.
  const clamp = (value: number) => Math.min(100, Math.max(0, value));
  return {
    mapX: clamp(((clientX - box.left) / box.width) * 100),
    mapY: clamp(((clientY - box.top) / box.height) * 100),
  };
}

/**
 * One waypoint.
 *
 * The party's own pin is marked rather than being a separate element, because
 * it *is* one of these — the join of `partyLocation` onto the list. A second
 * independent pin is the thing that could drift.
 */
function Pin({
  waypoint,
  here,
  chosen,
  onChoose,
}: {
  readonly waypoint: Waypoint;
  readonly here: boolean;
  readonly chosen?: boolean;
  /** Present when the pin can be clicked to ask about travelling there. */
  readonly onChoose?: () => void;
}) {
  const marker = (
    <>
      <span
        aria-hidden
        className={`block h-3 w-3 rounded-full border-2 border-white shadow ${
          here ? 'bg-emerald-500' : chosen ? 'bg-amber-500' : 'bg-neutral-500'
        }`}
      />
      <span
        className={`absolute left-4 top-0 whitespace-nowrap rounded bg-white/85 px-1 text-[0.65rem] dark:bg-neutral-900/85 ${
          here || chosen ? 'font-semibold' : ''
        } ${onChoose ? 'underline decoration-dotted underline-offset-2' : ''}`}
      >
        {waypoint.name}
        {here && ' — you are here'}
      </span>
    </>
  );
  return (
    <span
      className="absolute -translate-x-1/2 -translate-y-1/2"
      style={{ left: `${waypoint.mapX}%`, top: `${waypoint.mapY}%` }}
    >
      {onChoose ? (
        <button
          type="button"
          aria-label={`Travel to ${waypoint.name}`}
          title={`Travel to ${waypoint.name}…`}
          onClick={(event) => {
            // The Owner's click on the image places a waypoint; a click ON a pin must not also do that.
            event.stopPropagation();
            onChoose();
          }}
          className="relative block cursor-pointer p-2"
        >
          {marker}
        </button>
      ) : (
        marker
      )}
    </span>
  );
}

/**
 * A combatant's token, for a fight that opted into real positions.
 *
 * **Only drawn for a combatant who actually has one.** Positioning is opt-in
 * per encounter — a fight the Owner never gave a scale keeps rendering as
 * occupancy prose alone, exactly as it always has, and even a scaled fight
 * shows nobody a token until they declare their first `move` (free
 * placement). Absent is the ordinary case, not a bug to paper over with an
 * invented starting square.
 */
function CombatantTokens({ combat }: { readonly combat: CombatView }) {
  const placed = combat.combatants.filter((c) => c.position);
  if (placed.length === 0) return null;
  return (
    <>
      {placed.map((c) => (
        <span
          key={c.id}
          className="absolute -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${c.position!.mapX}%`, top: `${c.position!.mapY}%` }}
        >
          <span
            aria-hidden
            className={`block h-3 w-3 rounded-full border-2 border-white shadow ${
              c.side === 'party' ? 'bg-emerald-500' : c.side === 'enemy' ? 'bg-red-600' : 'bg-neutral-500'
            } ${c.conscious ? '' : 'opacity-40'}`}
          />
          <span className="absolute left-4 top-0 whitespace-nowrap rounded bg-white/85 px-1 text-[0.65rem] dark:bg-neutral-900/85">
            {c.name}
          </span>
        </span>
      ))}
    </>
  );
}

/**
 * Who is standing on whom, as the engine holds it.
 *
 * Occupancy, always shown — every fight has zones, whether or not it also
 * opted into a scaled map with real positions (`CombatantTokens`, above).
 * Theatre of the Mind is a first-class surface, not a fallback for a fight
 * that hasn't been given coordinates.
 */
function ZoneOccupancy({ combat }: { readonly combat: CombatView }) {
  const engaged = combat.combatants
    .map((defender) => ({ defender, prose: zoneProse(combat, defender.id) }))
    .filter((row) => row.prose !== '');

  if (engaged.length === 0) {
    return <p className="text-xs text-neutral-500 dark:text-neutral-400">Nobody is engaged yet.</p>;
  }

  return (
    <ul className="space-y-0.5 text-xs">
      {engaged.map(({ defender, prose }) => (
        <li key={defender.id}>
          <span className="font-medium">{defender.name}</span> is engaged by {prose}
        </li>
      ))}
    </ul>
  );
}
